import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RequirementCommonHeader } from "../layout/RequirementCommonHeader";
import { Requirement } from "sr/models/Requirement";
import { getRequirementDetails, deleteRequirement } from "sr/utils/api/corporate/requirements";
import { setPreview } from "../helpers/setPreview";
import { getFile } from "sr/utils/api/getFile";
import { toast } from "react-toastify";
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import AttachmentList from 'sr/helpers/ui-components/AttachmentList'
import { getBusinessType } from 'sr/utils/api/masterData/getBusinessType'
import { serviceTypes as serviceTypeStatic } from '../requirement-post/RequirementJsonData'
import { useIntl } from 'react-intl'
import { getAreaType } from 'sr/utils/api/masterData/getAreaType'
import { getFrequency } from 'sr/utils/api/masterData/getFrequency'
import { getDuration } from 'sr/utils/api/masterData/getDuration'

export const RequirementDetails = () => {
  const intl = useIntl();
  const { id: reqId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [req, setReq] = useState<Requirement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState<any[]>([]);
  const [loader, setLoader] = useState("");
  const setLoaderBool = (v: boolean) => setLoader(v ? "loading" : "");
  const [businessTypeOptions, setBusinessTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [areaLabelMap, setAreaLabelMap] = useState<Record<string, string>>({});
  const [freqLabelMap, setFreqLabelMap] = useState<Record<string, string>>({});
  const [durationLabelMap, setDurationLabelMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!reqId) return;
      setLoading(true);
      const res = await getRequirementDetails(reqId);
      if (res?.success) {
        setReq(res.data as any);
      } else {
        setReq(null);
      }
      setLoading(false);
    };
    fetchData();
  }, [reqId]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const bt = await getBusinessType();
        if (bt?.success && Array.isArray(bt.data)) {
          setBusinessTypeOptions(bt.data.map((i:any)=> ({ value:i.value, label:i.label })));
        }
      } catch {}
      setServiceTypeOptions(serviceTypeStatic.map((s:any)=> ({ value:s.value, label:s.label })));
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const a = await getAreaType();
        if (a?.success && Array.isArray(a.data)) {
          const m: Record<string, string> = {};
          a.data.forEach((i: any) => (m[i.value] = i.label));
          setAreaLabelMap(m);
        }
      } catch {}
      try {
        const f = await getFrequency();
        if (f?.success && Array.isArray(f.data)) {
          const m: Record<string, string> = {};
          f.data.forEach((i: any) => (m[i.value] = i.label));
          setFreqLabelMap(m);
        }
      } catch {}
      try {
        const d = await getDuration();
        if (d?.success && Array.isArray(d.data)) {
          const m: Record<string, string> = {};
          d.data.forEach((i: any) => (m[i.value] = i.label));
          setDurationLabelMap(m);
        }
      } catch {}
    };
    loadMasters();
  }, []);

  useEffect(() => {
    const buildPreviews = async () => {
      if (!req || !req.attachments?.length) return;

      const previews: any[] = [];

      const imageFiles = req.attachments.filter((a) =>
        /\.(jpg|jpeg|png|gif)$/i.test(a.url)
      );
      if (imageFiles.length > 0) {
        await new Promise<void>((resolve) => {
          setPreview({
            type: "images",
            files: imageFiles.map((a) => a.url) as any,
            setPreviewImages: (images: any[]) => {
              previews.push(
                ...images.map((img, idx) => ({
                  name: imageFiles[idx]?.url || `image-${idx}`,
                  blob: img.blob || img,
                }))
              );
              resolve();
            },
            setLoader: setLoaderBool,
          });
        });
      }

      const docFiles = req.attachments.filter((a) => /\.pdf$/i.test(a.url));
      for (const doc of docFiles) {
        const resolvedUrl = await getFile(doc.url, "url");
        if (resolvedUrl) {
          previews.push({ name: doc.url, blob: resolvedUrl });
        }
      }

      setAttachmentPreviews(previews);
    };

    buildPreviews();
  }, [req]);

  const formatAddress = (req: Requirement) => {
    if (!req.address) return "—";
    const { address_line_1, address_line_2, city, state, country, postal } = req.address;
    return [address_line_1, address_line_2, city, state, country, postal]
      .filter(Boolean)
      .join(", ");
  };

  if (loading) {
    return (
      <div className="py-3">
        <RequirementCommonHeader title="Requirement Details" />
        <div className="">
          <RequirementCardSkeleton />
        </div>
      </div>
    );
  }

  if (!req) {
    return (
      <div className="py-3">
        <RequirementCommonHeader title="Requirement Details" />
        <div className="p-6">
          <p className="text-gray-600">Requirement not found.</p>
          <button
            className="mt-4 px-4 py-2 bg-[#0078ae] text-white rounded-lg"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-3">
      <RequirementCommonHeader
        title="Requirement Details"
        actions={
          <>
            <button
              onClick={() =>
                navigate(`/corporate/requirement-post/review/${reqId}`)
              }
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>

            <button
              onClick={async () => {
                if (!reqId) return;
                if (
                  !window.confirm(
                    "Are you sure you want to delete this requirement?"
                  )
                )
                  return;

                setLoading(true);
                const res = await deleteRequirement(reqId);
                setLoading(false);

                if (res?.success) {
                  toast.success("Requirement deleted successfully");
                  navigate("/corporate/requirements");
                }
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </>
        }
      />

      <div className="p-6 flex flex-col md:flex-row gap-6 rounded-[20px] mb-6 border border-2xl">
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-[#0078ae]">
                {req.title}
              </h3>
              {req?.address && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                  {formatAddress(req)}
                </span>
              )}
            </div>
            <StatusPill status={req.status}>
              {req.status.replace("_", " ")}
            </StatusPill>
          </div>

          <p className="text-sm text-gray-600 mb-4">{req.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            <MetaBox label="Business Types" value={(req.business_types || []).map((v:any)=> {
              const opt = businessTypeOptions.find(o=> o.value===v);
              const lbl = opt?.label || v;
              return intl.formatMessage({id: lbl, defaultMessage: lbl});
            })} />
            <MetaBox label="Services" value={(req.service_type || []).map((v:any)=> {
              const opt = serviceTypeOptions.find(o=> o.value===v);
              const lbl = opt?.label || v;
              return intl.formatMessage({id: lbl, defaultMessage: lbl});
            })} />
            <MetaBox label="Area" value={req.area || "—"} multi={false} />
            <MetaBox label="Area Type" value={req.area_type ? intl.formatMessage({ id: areaLabelMap[String(req.area_type)] || String(req.area_type), defaultMessage: String(req.area_type) }) : "—"} multi={false} />
            <MetaBox label="Frequency" value={req.frequency ? intl.formatMessage({ id: freqLabelMap[String(req.frequency)] || String(req.frequency), defaultMessage: String(req.frequency) }) : "—"} multi={false} />
            <MetaBox label="Duration" value={req.duration ? intl.formatMessage({ id: durationLabelMap[String(req.duration)] || String(req.duration), defaultMessage: String(req.duration) }) : "—"} multi={false} />
            <MetaBox
              label="Start Date"
              value={req.start_date ? new Date(req.start_date).toLocaleDateString() : "—"}
              multi={false}
            />
            {req.end_date && (
              <MetaBox label="End Date" value={new Date(req.end_date).toLocaleDateString()} multi={false} />
            )}
            <MetaBox label="Budget" value={req.budget || "—"} multi={false} />
          </div>

          {req.special_instructions && (
            <div className="bg-gray-50 px-3 py-2 rounded-lg border mb-4">
              <p className="text-xs text-gray-500 mb-2">Special Instructions</p>
              <p className="text-sm text-gray-800">{req.special_instructions}</p>
            </div>
          )}

          {req.documents_required && req.documents_required.length > 0 && (
            <div className="bg-gray-50 px-3 py-2 rounded-lg border mb-4">
              <p className="text-xs text-gray-500 mb-2">Documents Required</p>
              <div className="text-sm text-gray-800">
                {req.documents_required.map((doc, i) => (
                  <span
                    key={i}
                    className="inline-flex mr-1 items-center px-2 py-1 rounded-md text-xs font-medium bg-[#ecf8fd] border border-[#c2e7f9] text-[#0078ae]"
                  >
                    {doc?.doc_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(attachmentPreviews.length > 0 || (req.attachments && req.attachments.length > 0)) && (
            <div className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                {attachmentPreviews
                  .filter((f: any) => /\.(jpg|jpeg|png|gif)$/i.test(String(f?.name || '')))
                  .map((file: any, i: number) => (
                    <img
                      key={`img-${i}`}
                      src={file.blob}
                      alt="attachment"
                      className="w-24 h-24 object-cover rounded-lg border"
                    />
                  ))}
              </div>
              <AttachmentList items={req.attachments || []} />
            </div>
          )}

          {req.updatedAt && (
            <div className="flex items-center text-xs text-gray-500 mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Updated {new Date(req.updatedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RequirementCardSkeleton = () => (
  <div className="p-6 flex flex-col md:flex-row gap-6 rounded-[20px] mb-6 border border-2xl animate-pulse">
    <div className="flex-1">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          <div className="h-6 bg-gray-200 rounded-full w-24"></div>
        </div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
    </div>
  </div>
);

/* MetaBox: using shared component imported above */
